import { ApolloError, useMutation } from "@apollo/client";
import { getErrorMessage } from "@metaphore/error-handling";
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import React, { ReactElement, useRef } from "react";

import {
  OptimizeTextInput,
  OptimizeTextOperation,
} from "../../generated/gql/graphql";
import DeleteIcon from "../icons/fill/delete";
import SendIcon from "../icons/fill/send";

import { CanvasObjectText } from "./canvas-object-text";
import { OptimizeTextGQL } from "./use-optimize-text";
import { TranslateTextGQL } from "./use-translate-text";

export interface TextAiToolsProps {
  textObject: CanvasObjectText;
  onTextChange: (updatedText: string) => void;
}

enum ToolStatus {
  Idle,
  Running,
  Completed,
}

export function TextAiTools({
  textObject,
  onTextChange,
}: TextAiToolsProps): ReactElement {
  const abortControllerRef = useRef<AbortController | null>(null);
  const [selectedTool, setSelectedTool] = React.useState<string>("translate");
  const [toolState, setToolState] = React.useState<ToolStatus>(ToolStatus.Idle);
  const [suggestedText, setSuggestedText] = React.useState<string | null>(null);
  const [toolError, setToolError] = React.useState<string | null>(null);

  const [targetLanguage, setTargetLanguage] = React.useState<string>("en");
  const [promptMessage, setPromptMessage] = React.useState<string>("");
  const [changeToneFlavor, setChangeToneFlavor] =
    React.useState<string>("formal");

  const [translateText] = useMutation(TranslateTextGQL, {});
  const [optimizeText] = useMutation(OptimizeTextGQL, {});

  async function handleExecute(): Promise<void> {
    setToolState(ToolStatus.Running);
    let callMutation: (() => Promise<string | undefined>) | undefined;
    if (selectedTool === "translate") {
      callMutation = async () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        const result = await translateText({
          variables: {
            input: { text: textObject.text.content, targetLanguage },
          },
          context: {
            fetchOptions: {
              signal: abortControllerRef.current?.signal,
            },
          },
        });
        return result.data?.translateText.translatedText;
      };
    } else {
      // Execute other tools without additional parameters
      let operation;
      switch (selectedTool) {
        case "spellCheck":
          operation = OptimizeTextOperation.SpellCheck;
          break;
        case "shorten":
          operation = OptimizeTextOperation.Shorten;
          break;
        case "prompt":
          operation = OptimizeTextOperation.Prompt;
          break;
        // case "clarify":
        //   operation = OptimizeTextOperation.Clarify;
        //   break;
        case "changeTone":
          {
            if (changeToneFlavor === "formal") {
              operation = OptimizeTextOperation.ChangeToneFormal;
            } else {
              operation = OptimizeTextOperation.ChangeToneInformal;
            }
          }
          break;
      }
      if (operation) {
        let requestParams: OptimizeTextInput = {
          text: textObject.text.content,
          operation,
        };

        if (promptMessage.length > 0) {
          requestParams = {
            ...requestParams,
            prompt: promptMessage,
          };
        }

        callMutation = async () => {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
          abortControllerRef.current = new AbortController();
          const result = await optimizeText({
            variables: {
              input: requestParams,
            },
            context: {
              fetchOptions: {
                signal: abortControllerRef.current?.signal,
              },
            },
          });
          return result.data?.optimizeText.optimizedText;
        };
      }
    }

    if (callMutation) {
      try {
        const updatedText = await callMutation();
        if (updatedText) {
          setSuggestedText(updatedText);
          setToolState(ToolStatus.Completed);
        } else {
          setToolState(ToolStatus.Idle);
        }
      } catch (error) {
        if (
          error instanceof ApolloError &&
          error.cause?.name === "AbortError"
        ) {
          // Okay, request was aborted, do not show an error message
        } else {
          setToolError(getErrorMessage(error));
        }
        setToolState(ToolStatus.Idle);
      }
    } else {
      setToolState(ToolStatus.Idle);
    }
  }

  let tool;
  switch (selectedTool) {
    case "translate":
      tool = (
        <>
          <Alert
            sx={{
              "& .MuiAlert-message": {
                padding: 0,
                width: "100%",
              },
            }}
            className="bg-[#F9EFFF] p-0 text-[#8958BD] text-[14px]"
            icon={false}
            severity="info"
          >
            <div className="flex gap-[10px] justify-between">
              <span>Select the target language for AI translation.</span>
              <div className="flex gap-[10px] justify-between">
                <FormControl
                  style={{ width: "200px" }}
                  sx={{
                    "& fieldset": {
                      borderColor: "#B989ED",
                    },
                  }}
                  disabled={toolState === ToolStatus.Running}
                >
                  <Select
                    sx={{
                      "& .MuiSelect-select": {
                        paddingTop: "6.5px",
                        paddingBottom: "6.5px",
                        background: "white",
                      },
                    }}
                    size="small"
                    value={targetLanguage}
                    className="text-[#8958BD] border-[#B989ED]"
                    onChange={(e) => setTargetLanguage(e.target.value)}
                  >
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="de">German</MenuItem>
                    <MenuItem value="fr">French</MenuItem>
                    <MenuItem value="es">Spanish</MenuItem>
                    <MenuItem value="zh">Chinese</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  className="bg-[#8958BD] p-[10px] min-w-fit"
                  variant="contained"
                  color="primary"
                  onClick={handleExecute}
                >
                  {toolState === ToolStatus.Running ? (
                    <CircularProgress size={16} className="text-white" />
                  ) : (
                    <SendIcon />
                  )}
                </Button>
              </div>
            </div>
          </Alert>
        </>
      );
      break;
    case "spellCheck":
      tool = (
        <Alert
          sx={{
            "& .MuiAlert-message": {
              padding: 0,
              width: "100%",
            },
          }}
          className="bg-[#F9EFFF] p-0 text-[#8958BD] text-[14px]"
          icon={false}
          severity="info"
        >
          <div className="flex gap-[10px] justify-between">
            <span>
              The AI will check the text content for spelling errors and suggest
              corrections.
            </span>
            <Button
              className="bg-[#8958BD] p-[10px] min-w-fit"
              variant="contained"
              color="primary"
              onClick={handleExecute}
            >
              {toolState === ToolStatus.Running ? (
                <CircularProgress size={16} className="text-white" />
              ) : (
                <SendIcon />
              )}
            </Button>
          </div>
        </Alert>
      );
      break;
    case "prompt":
      tool = (
        <Alert
          sx={{
            "& .MuiAlert-message": {
              padding: 0,
              width: "100%",
            },
          }}
          className="bg-[#F9EFFF] p-0 text-[#8958BD] text-[14px]"
          icon={false}
          severity="info"
        >
          <div className="flex gap-[10px] justify-between">
            <TextField
              sx={{
                "& .MuiInputBase-input": {
                  paddingTop: "7px",
                  paddingBottom: "6px",
                },
              }}
              fullWidth
              size="small"
              placeholder="Enter your prompt..."
              value={promptMessage}
              onChange={(e) => setPromptMessage(e.target.value)}
            />
            <Button
              className="bg-[#8958BD] p-[10px] min-w-fit"
              variant="contained"
              color="primary"
              onClick={handleExecute}
            >
              {toolState === ToolStatus.Running ? (
                <CircularProgress size={16} className="text-white" />
              ) : (
                <SendIcon />
              )}
            </Button>
          </div>
        </Alert>
      );
      break;
    case "shorten":
      tool = (
        <Alert
          sx={{
            "& .MuiAlert-message": {
              padding: 0,
              width: "100%",
            },
          }}
          className="bg-[#F9EFFF] p-0 text-[#8958BD] text-[14px]"
          icon={false}
          severity="info"
        >
          <div className="flex gap-[10px] justify-between">
            <span>Ai will enhance the text according to your settings.</span>
            <Button
              className="bg-[#8958BD] p-[10px] min-w-fit"
              variant="contained"
              color="primary"
              onClick={handleExecute}
            >
              {toolState === ToolStatus.Running ? (
                <CircularProgress size={16} className="text-white" />
              ) : (
                <SendIcon />
              )}
            </Button>
          </div>
        </Alert>
      );
      break;
    case "changeTone":
      tool = (
        <>
          <Alert
            sx={{
              "& .MuiAlert-message": {
                padding: 0,
                width: "100%",
              },
            }}
            className="bg-[#F9EFFF] p-0 text-[#8958BD] text-[14px]"
            icon={false}
            severity="info"
          >
            <div className="flex gap-[10px] justify-between">
              <span>Select the ton the AI will apply to the text.</span>
              <div className="flex gap-[10px] justify-between">
                <FormControl
                  style={{ width: "200px" }}
                  sx={{
                    "& fieldset": {
                      borderColor: "#B989ED",
                    },
                  }}
                >
                  <Select
                    sx={{
                      "& .MuiSelect-select": {
                        paddingTop: "6.5px",
                        paddingBottom: "6.5px",
                        background: "white",
                      },
                    }}
                    disabled={toolState === ToolStatus.Running}
                    className="text-[#8958BD] border-[#B989ED]"
                    size="small"
                    value={changeToneFlavor}
                    onChange={(e) => setChangeToneFlavor(e.target.value)}
                  >
                    <MenuItem value="formal">Formal</MenuItem>
                    <MenuItem value="informal">Informal</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  className="bg-[#8958BD] p-[10px] min-w-fit"
                  variant="contained"
                  color="primary"
                  onClick={handleExecute}
                >
                  {toolState === ToolStatus.Running ? (
                    <CircularProgress size={16} className="text-white" />
                  ) : (
                    <SendIcon />
                  )}
                </Button>
              </div>
            </div>
          </Alert>
        </>
      );
      break;
  }

  function handleToolChange(newTool: string): void {
    setSelectedTool(newTool);
    setToolState(ToolStatus.Idle);
  }

  function handleCancel(): void {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setToolState(ToolStatus.Idle);
  }

  function handleDeclineSuggestion(): void {
    setToolState(ToolStatus.Idle);
  }

  function handleAcceptSuggestion(): void {
    if (suggestedText) {
      onTextChange(suggestedText);
    }
    setToolState(ToolStatus.Idle);
  }

  return (
    <div className="h-full flex flex-col">
      <ToggleButtonGroup
        value={selectedTool}
        onChange={(_event, newValue) => {
          if (!newValue) {
            // Do not allow deselecting the currently selected tool, require selecting another tool instead
            return;
          }
          handleToolChange(newValue);
        }}
        exclusive={true}
        disabled={toolState === ToolStatus.Running}
        sx={{
          "& .Mui-selected": {
            color: "white!important",
            background: "#b989ed!important",
          },
        }}
      >
        <ToggleButton
          className="w-[110px] text-center h-[36px] border-0 !bg-[#f9efff] capitalize text-[#8958bd] text-[14px]"
          value="translate"
        >
          Translate
        </ToggleButton>
        <ToggleButton
          className="w-[110px] text-center h-[36px] border-0 !bg-[#f9efff] capitalize text-[#8958bd] text-[14px] !border-l !border-[#b989ed4d] border-solid"
          value="spellCheck"
        >
          Spell Check
        </ToggleButton>
        <ToggleButton
          className="w-[110px] text-center h-[36px] border-0 !bg-[#f9efff] capitalize text-[#8958bd] text-[14px] !border-l !border-[#b989ed4d] border-solid"
          value="shorten"
        >
          Enhance
        </ToggleButton>
        {/* <ToggleButton value="clarify">Clarify</ToggleButton> */}
        <ToggleButton
          className="w-[110px] text-center h-[36px] border-0 !bg-[#f9efff] capitalize text-[#8958bd] text-[14px] !border-l !border-[#b989ed4d] border-solid !rounded-tr-[4px] !rounded-br-[4px]"
          value="changeTone"
        >
          Tone
        </ToggleButton>
        <ToggleButton
          className="w-[110px] !rounded-[4px] !ml-auto text-center h-[36px] !border-0 !bg-[#f9efff] capitalize text-[#8958bd] text-[14px]"
          value="prompt"
        >
          Prompt
        </ToggleButton>
      </ToggleButtonGroup>
      <div className="flex-grow mt-3 flex flex-col justify-between gap-3">
        {toolState === ToolStatus.Completed && (
          <>
            <div>
              <Alert
                sx={{
                  "& .MuiAlert-message": {
                    padding: 0,
                    width: "100%",
                  },
                }}
                className="bg-[#F9EFFF] p-[10px] text-[#8958BD] text-[14px]"
                icon={false}
                severity="success"
              >
                <span className="text-black">Your result:</span>
                <br />
                {suggestedText}
              </Alert>
            </div>
            <div className="flex justify-between bg-[#F9EFFF] p-[10px]">
              <Button
                variant="text"
                color="secondary"
                className="text-[#4A4A4A] font-bold hover:bg-transparent"
                onClick={handleDeclineSuggestion}
              >
                Discard
                <DeleteIcon className="ml-[5px]" />
              </Button>
              <Button
                className="text-[#4A4A4A] border-0 shadow bg-white hover:bg-white hover:border-0"
                variant="outlined"
                color="success"
                onClick={handleAcceptSuggestion}
              >
                Save
              </Button>
            </div>
          </>
        )}
        {toolState !== ToolStatus.Completed && (
          <Alert
            sx={{
              "& .MuiAlert-message": {
                padding: 0,
                width: "100%",
              },
            }}
            className="bg-[#F9EFFF] p-[10px] text-[#8958BD] text-[14px]"
            icon={false}
            severity="success"
          >
            <div>{tool}</div>
            <div className="flex justify-between">
              <div>
                {toolError && <Alert severity="error">{toolError}</Alert>}
              </div>
              {/* {toolState === ToolStatus.Running && (
                <Button
                  variant="outlined"
                  color="success"
                  className="text-[#4A4A4A] border-0 shadow bg-white hover:bg-white hover:border-0"
                  onClick={handleCancel}
                >
                  Cancel
                  {toolState === ToolStatus.Running && (
                    <CircularProgress size={17} className="ml-[5px]" />
                  )}
                </Button>
              )} */}
            </div>
          </Alert>
        )}
      </div>
    </div>
  );
}
